import Router from 'next/router';
import { localeLanguageCodes } from 'shared-locales';
import { useDateNow, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import { CardExtension } from 'components/common/tasks';
import { CoursesTileContent } from 'components/courses';
import { CoursePicture } from 'components/courses/common';
import {
  CoursesTilePaths,
  CoursesTileProgress,
  CoursesTilePublishStatus,
  CoursesUserAction
} from '.';

import type { CourseProgress } from 'shared-types';
import type { CourseInView } from './Courses';

interface Props {
  course: CourseInView;
  progressData?: CourseProgress;
  updateCourses: () => Promise<void>;
}

const CoursesTile = ({ course, progressData, updateCourses }: Props) => {
  const { dateNow } = useDateNow();

  const {
    language,
    messages,
    serverConfig: { enableCoursePicture },
    theme,
    user
  } = useReduxState();

  const handleCourseClick = () => {
    Router.push(`/courses/[...courseParams]`, `/courses/${course?.slug}`);
  };

  const handleCourseKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.target !== ev.currentTarget) return;
    // check if enter or spacebar
    if (ev.keyCode === 13 || ev.keyCode === 32) {
      ev.preventDefault();
      handleCourseClick();
    }
  };

  return (
    <div
      className="course"
      onClick={handleCourseClick}
      onKeyDown={handleCourseKeyDown}
      role="button"
      tabIndex={0}
    >
      {enableCoursePicture ? (
        <div className="picture">
          <CoursePicture
            position={course.picturePosition}
            size={640}
            url={course.picture}
          />
        </div>
      ) : null}
      <div className="content">
        <CoursesTileContent
          course={course}
          preferredLanguageVersion={
            course.courseCompletion?.courseLanguageVersion ||
            localeLanguageCodes[language]
          }
          showDropdown={
            course.isOwnerOrSuperadmin ||
            course.isAdmin ||
            course.isAddedUser ||
            (course.adminAccess === 'copyable' &&
              user?.roles?.includes('admin') &&
              user?.permissions?.includes('create-courses'))
          }
          updateCourses={updateCourses}
        />

        <div className="footer">
          <div className="footer-content">
            <div className="progress">
              {progressData ? (
                <CoursesTileProgress
                  completionEnabled={course.completionEnabled}
                  id={`courses-tile-progress-${course.id}`}
                  progressData={progressData}
                />
              ) : null}
            </div>

            <CoursesUserAction course={course} />
          </div>
        </div>
      </div>

      <CoursesTilePublishStatus
        endDate={course.endDate as string}
        openingHours={course.openingHours}
        startDate={course.startDate as string}
      />

      {course.deadline && progressData?.completion?.status !== 'success' ? (
        <CardExtension
          icon={['far', 'clock']}
          innerStyle={{
            color:
              Date.parse(course.deadline) < dateNow
                ? theme.dangerColor
                : theme.warningStandaloneColor
          }}
        >
          <div className="deadline-label">
            {`${messages.courseTargetSchedule}: ${getFormattedDate({
              date: course.deadline
            })}`}
          </div>
        </CardExtension>
      ) : null}

      <CoursesTilePaths
        courseIsCompleted={progressData?.completion?.status === 'success'}
        paths={course.paths}
      />

      <style jsx>{`
        .course {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          cursor: pointer;
          outline: 0;
          position: relative;
          transition: box-shadow 0.3s;
        }
        .course:focus,
        .course:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .course:focus-visible {
          outline: revert;
        }
        .picture {
          border-radius: ${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0;
          height: 140px;
          overflow: hidden;
          position: relative;
        }
        .content {
          display: flex;
          flex-direction: column;
          min-height: 200px;
          padding: 15px;
        }
        .footer {
          margin-top: auto;
          padding-top: 20px;
        }
        .footer-content {
          align-items: center;
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
        .deadline-label {
          display: -webkit-box;
          flex-grow: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
      `}</style>
    </div>
  );
};

export default CoursesTile;
